import { useState } from "react";
import { Eye, EyeOff, ArrowRight, ArrowLeft, Lock, User, Key, Shield } from "lucide-react";

export default function LoginPage({ onLoginSuccess, error: externalError }) {
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Show external error if present
  const displayError = externalError || error;

  const handleStep1Submit = (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password");
      return;
    }
    setError("");
    setStep(2);
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!clientId.trim() || !clientSecret.trim()) {
      setError("Please enter both Client ID and Client Secret");
      return;
    }
    
    setError("");
    setLoading(true);
    
    // Pass credentials up to parent for authentication
    try {
      await onLoginSuccess({
        username: username.trim(),
        password: password.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      });
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setError("");
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(135deg, #DCDFE2 0%, #C8CDD1 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Logo and tagline */}
        <div className="text-center mb-8">
          <img
            src="/wokelo-logo.png"
            alt="Wokelo"
            className="h-12 mx-auto mb-3"
            data-testid="login-logo"
          />
          <p className="text-[13px] text-[#6B7280]" style={{ fontFamily: 'Inter, sans-serif' }}>
            News Monitoring Platform
          </p>
        </div>

        {/* Card */}
        <div 
          className="wokelo-card p-8 fade-in"
          style={{ background: '#FFFFFF' }}
        >
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className={`w-2 h-2 rounded-full transition-all ${step === 1 ? 'bg-[#0A0A0A] scale-125' : 'bg-[#DCDFE2]'}`} />
            <span className="text-xs text-[#6B7280]">Step {step} of 2</span>
            <div className={`w-2 h-2 rounded-full transition-all ${step === 2 ? 'bg-[#0A0A0A] scale-125' : 'bg-[#DCDFE2]'}`} />
          </div>

          {/* Step 1: Username & Password */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-5 slide-in-right">
              <div className="text-center mb-6">
                <h1 className="text-xl font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Welcome back
                </h1>
                <p className="text-sm text-[#6B7280] mt-1">
                  Sign in to access your dashboard
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="small-caps flex items-center gap-1.5">
                  <User size={12} />
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your username"
                  data-testid="login-username"
                  className="w-full h-11 px-4 rounded-lg border border-[#DCDFE2] bg-[#F4F5F6] text-sm text-[#0A0A0A] placeholder:text-[#9AA0A6] focus:bg-white focus:border-[#0A0A0A] transition-colors"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <label className="small-caps flex items-center gap-1.5">
                  <Lock size={12} />
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter your password"
                    data-testid="login-password"
                    className="w-full h-11 px-4 pr-11 rounded-lg border border-[#DCDFE2] bg-[#F4F5F6] text-sm text-[#0A0A0A] placeholder:text-[#9AA0A6] focus:bg-white focus:border-[#0A0A0A] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0A6] hover:text-[#3A3A3A] transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {displayError && (
                <div className="text-sm text-[#A63446] bg-[#A6344610] border border-[#A63446] rounded-lg px-3 py-2">
                  {displayError}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-continue-btn"
                className="w-full h-11 bg-[#0A0A0A] text-white rounded-lg text-sm font-medium hover:bg-[#3A3A3A] transition-colors flex items-center justify-center gap-2 group"
              >
                Continue
                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
            </form>
          )}

          {/* Step 2: Client ID & Secret */}
          {step === 2 && (
            <form onSubmit={handleStep2Submit} className="space-y-5 slide-in-right">
              <button
                type="button"
                onClick={goBack}
                data-testid="login-back-btn"
                className="flex items-center gap-1.5 text-sm text-[#6B7280] hover:text-[#0A0A0A] transition-colors mb-4"
              >
                <ArrowLeft size={14} />
                Back
              </button>

              <div className="text-center mb-6">
                <h1 className="text-xl font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  API Credentials
                </h1>
                <p className="text-sm text-[#6B7280] mt-1">
                  Enter your Wokelo API credentials
                </p>
              </div>

              {/* Signed in as badge */}
              <div className="flex items-center gap-2 px-3 py-2 bg-[#F4F5F6] border border-[#DCDFE2] rounded-lg mb-4">
                <div className="w-6 h-6 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                  <User size={12} className="text-white" />
                </div>
                <span className="text-sm text-[#3A3A3A]">
                  Signing in as <strong>{username}</strong>
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="small-caps flex items-center gap-1.5">
                  <Key size={12} />
                  Client ID
                </label>
                <input
                  type="text"
                  value={clientId}
                  onChange={(e) => {
                    setClientId(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your Client ID"
                  data-testid="login-client-id"
                  className="w-full h-11 px-4 rounded-lg border border-[#DCDFE2] bg-[#F4F5F6] text-sm text-[#0A0A0A] placeholder:text-[#9AA0A6] font-mono focus:bg-white focus:border-[#0A0A0A] transition-colors"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <label className="small-caps flex items-center gap-1.5">
                  <Shield size={12} />
                  Client Secret
                </label>
                <div className="relative">
                  <input
                    type={showSecret ? "text" : "password"}
                    value={clientSecret}
                    onChange={(e) => {
                      setClientSecret(e.target.value);
                      setError("");
                    }}
                    placeholder="Enter your Client Secret"
                    data-testid="login-client-secret"
                    className="w-full h-11 px-4 pr-11 rounded-lg border border-[#DCDFE2] bg-[#F4F5F6] text-sm text-[#0A0A0A] placeholder:text-[#9AA0A6] font-mono focus:bg-white focus:border-[#0A0A0A] transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0A6] hover:text-[#3A3A3A] transition-colors"
                  >
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {displayError && (
                <div className="text-sm text-[#A63446] bg-[#A6344610] border border-[#A63446] rounded-lg px-3 py-2">
                  {displayError}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-11 bg-[#0A0A0A] text-white rounded-lg text-sm font-medium hover:bg-[#3A3A3A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-[#9AA0A6] mt-6">
          Secure authentication via OAuth 2.0
        </p>
      </div>
    </div>
  );
}
